import { IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { ArdoqError, formatApiErrorWithTraceId } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import { showToast, ToastType } from '@ardoq/status-ui';

const DEFAULT_ERROR_MESSAGE = `Because of a technical issue, we couldn't load this table's data.`;

/**
 * Fail the SSRM block and toast the (localized) error with a trace-id support
 * line when available. Callers own error detection — for a `Result<T>` fetch
 * that means an `isArdoqError` check, for a discriminated response a
 * `status === 'error'` check.
 */
export const applyServerSideError = ({
  params,
  error,
  i18nLocale,
  errorToastMessage,
}: {
  params: IServerSideGetRowsParams;
  error: ArdoqError;
  i18nLocale: I18nLocale;
  errorToastMessage?: string;
}): void => {
  params.fail();
  showToast(
    formatApiErrorWithTraceId(
      error,
      i18nLocale,
      errorToastMessage ?? DEFAULT_ERROR_MESSAGE
    ),
    ToastType.ERROR
  );
};

/**
 * Push a fetched block into the grid: set the rows, self-correct the parent's
 * `allChildrenCount`, and toggle the no-rows overlay. Owns the common AG Grid
 * plumbing so consumers don't diverge. Domain side effects (e.g. merging
 * response metadata into grid context) belong at the call site, after this
 * returns.
 */
export const applyServerSideSuccess = <RowData>({
  params,
  rows,
  total,
  shouldShowNoRowsOverlay,
}: {
  params: IServerSideGetRowsParams;
  rows: RowData[];
  total: number;
  /** Override when to show the no-rows overlay. Defaults to an empty root-level result. */
  shouldShowNoRowsOverlay?: () => boolean;
}): void => {
  params.success({ rowData: rows, rowCount: total });

  const parentNode = params.parentNode;
  if (parentNode?.data) {
    parentNode.allChildrenCount = total;
    params.api.refreshCells({ rowNodes: [parentNode], force: true });
  }

  const showNoRowsOverlay = shouldShowNoRowsOverlay
    ? shouldShowNoRowsOverlay()
    : total === 0 && params.request.groupKeys.length === 0;
  if (showNoRowsOverlay) {
    params.api.showNoRowsOverlay();
  } else {
    params.api.hideOverlay();
  }
};
